import { useCallback, useRef, useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { LogOut, Globe, ChevronDown, Waypoints } from 'lucide-react'

const languages = [
  { code: 'ja', label: '日本語' },
  { code: 'en', label: 'English' },
] as const

export default function AppLayout() {
  const { t, i18n } = useTranslation()
  const { user, signOut } = useAuth()
  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const langMenuRef = useRef<HTMLDivElement>(null)

  const handleLogout = useCallback(async () => {
    await signOut()
  }, [signOut])

  const changeLanguage = useCallback(
    (code: string) => {
      i18n.changeLanguage(code)
      document.documentElement.lang = code
      setLangMenuOpen(false)
    },
    [i18n]
  )

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        langMenuRef.current &&
        !langMenuRef.current.contains(e.target as Node)
      ) {
        setLangMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const currentLabel =
    languages.find((l) => l.code === i18n.language)?.label ?? i18n.language

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <Waypoints className="h-6 w-6" style={{ color: '#3b82f6' }} />
              <span className="text-lg font-bold text-gray-900">
                {t('app.title')}
              </span>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <div className="relative" ref={langMenuRef}>
                <button
                  onClick={() => setLangMenuOpen((prev) => !prev)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{currentLabel}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {langMenuOpen && (
                  <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                          i18n.language === lang.code
                            ? 'text-primary-600 font-medium'
                            : 'text-gray-700'
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('app.logout')}
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{t('app.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
